import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { RefreshCw, GitBranch, ShieldCheck, TrendingUp, TrendingDown, AlertTriangle, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from './AuthDialog';

/**
 * Safely evaluate a simple arithmetic expression typed into an override cell
 * (e.g. "520+50" to add freight, "300*1.1"). Only digits, + - * / ( ) . and
 * whitespace are allowed — anything else returns null. Returns a positive
 * number or null when the expression is empty/invalid.
 */
export function evalMathExpr(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (!/^[0-9+\-*/().\s]+$/.test(s)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const val = Function(`"use strict"; return (${s});`)();
    if (typeof val !== 'number' || !isFinite(val)) return null;
    return Math.round(val * 100) / 100;
  } catch {
    return null;
  }
}

/**
 * POBranchPropagateMatrix
 * Embedded inside the PO Price-Change modal. Lists THIS PO's products as rows
 * and every branch as a column (scroll left↔right for branches, top↕down for
 * products). Each cell shows the branch's CURRENT capital; the base to push is
 * the NEW PO price for that product. Tick cells (or a whole row/column), then
 * propagate everything selected in one Admin-PIN confirm. Reuses the proven
 * /branch-prices/capital-scan + /propagate-capital engine.
 */
export default function POBranchPropagateMatrix({ products: reportProducts, sourcePoNumber, sourceBranchId }) {
  const [branches, setBranches] = useState([]);
  const [scanMap, setScanMap] = useState({}); // pid -> { branches: {bid: {current_capital, stock_on_hand}} }
  const [loading, setLoading] = useState(true);
  // selection: { [pid]: { [bid]: { include, override } } }
  const [sel, setSel] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  // Targets the server refused because they hold a NEWER DR than this PO.
  // Overwriting them would walk that branch's capital backwards, so the block
  // is enforced server-side; forcing needs a written reason.
  const [blocked, setBlocked] = useState([]);
  const [forceReason, setForceReason] = useState('');
  const [forcing, setForcing] = useState(false);

  const pids = useMemo(() => (reportProducts || []).map(p => p.product_id).filter(Boolean), [reportProducts]);
  // Use the raw PO unit cost as the propagation base — NOT new_capital which may
  // be the source branch's moving average (branch-specific, meaningless elsewhere).
  // Falls back to new_capital for historical report rows that pre-date po_unit_price.
  const baseByPid = useMemo(() => {
    const m = {};
    (reportProducts || []).forEach(p => {
      m[p.product_id] = Number(p.po_unit_price || p.new_capital || 0);
    });
    return m;
  }, [reportProducts]);

  const load = useCallback(async () => {
    if (!pids.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/branch-prices/capital-scan', {
        params: { product_ids: pids.join(','), limit: pids.length },
      });
      setBranches(res.data.branches || []);
      const map = {};
      (res.data.products || []).forEach(p => { map[p.product_id] = p; });
      setScanMap(map);
    } catch {
      toast.error('Failed to load branch capital');
    }
    setLoading(false);
  }, [pids]);

  useEffect(() => { load(); }, [load]);

  const curCapital = useCallback((pid, bid) => {
    const br = scanMap[pid]?.branches?.[bid];
    return br ? Number(br.current_capital || 0) : null;
  }, [scanMap]);

  const toggleCell = (pid, bid) => {
    setSel(prev => {
      const row = prev[pid] || {};
      const c = row[bid] || { include: false, override: '' };
      return { ...prev, [pid]: { ...row, [bid]: { ...c, include: !c.include } } };
    });
  };

  const setOverride = (pid, bid, val) => {
    setSel(prev => {
      const row = prev[pid] || {};
      const c = row[bid] || { include: false, override: '' };
      return { ...prev, [pid]: { ...row, [bid]: { include: true, override: val } } };
    });
  };

  const toggleRow = (pid) => {
    setSel(prev => {
      const row = prev[pid] || {};
      const allOn = branches.every(b => row[b.id]?.include);
      const next = {};
      branches.forEach(b => { next[b.id] = { include: !allOn, override: row[b.id]?.override || '' }; });
      return { ...prev, [pid]: next };
    });
  };

  const toggleColumn = (bid) => {
    setSel(prev => {
      const allOn = pids.every(pid => prev[pid]?.[bid]?.include);
      const next = { ...prev };
      pids.forEach(pid => {
        const row = next[pid] || {};
        next[pid] = { ...row, [bid]: { include: !allOn, override: row[bid]?.override || '' } };
      });
      return next;
    });
  };

  // Flattened selection for review/apply.
  const selectedItems = useMemo(() => {
    const items = [];
    (reportProducts || []).forEach(p => {
      const pid = p.product_id;
      const row = sel[pid] || {};
      const base = baseByPid[pid] || 0;
      branches.forEach(b => {
        const c = row[b.id];
        if (c?.include) {
          const ov = evalMathExpr(c.override);
          const newCap = ov && ov > 0 ? ov : base;
          items.push({ pid, productName: p.name, branch: b, base, override: ov, newCap, current: curCapital(pid, b.id) });
        }
      });
    });
    return items;
  }, [sel, reportProducts, branches, baseByPid, curCapital]);

  const selectedCount = selectedItems.length;

  const openReview = () => {
    if (!selectedCount) { toast.warning('Tick at least one branch cell to propagate'); return; }
    // Block any ticked cell whose typed override is an invalid math expression.
    const hasInvalid = (reportProducts || []).some(p => {
      const row = sel[p.product_id] || {};
      return branches.some(b => {
        const c = row[b.id];
        if (!c?.include) return false;
        const raw = (c.override ?? '').toString().trim();
        return raw !== '' && evalMathExpr(raw) == null;
      });
    });
    if (hasInvalid) { toast.warning('A price cell has an invalid calculation — fix the highlighted cell (e.g. 520+50)'); return; }
    if (selectedItems.some(it => it.newCap <= 0)) {
      toast.warning('Some selected products have no New PO price — type a per-branch price for those'); return;
    }
    setReviewOpen(true);
  };

  const nameOf = useCallback(
    (pid) => (reportProducts || []).find(p => p.product_id === pid)?.product_name || pid,
    [reportProducts]);

  /** One propagate call per product. Returns what went through, what failed,
   *  and what the server BLOCKED for a stale source DR. */
  const runPropagate = async (byPid, { force = false, reason = '' } = {}) => {
    let ok = 0, fail = 0;
    const stale = [];
    for (const pid of Object.keys(byPid)) {
      const group = byPid[pid];
      try {
        await api.post('/branch-prices/propagate-capital', {
          product_id: pid,
          base_capital: group.base,
          source_branch_id: sourceBranchId || '',
          source_po_number: sourcePoNumber || '',
          targets: group.targets,
          ...(force ? { force_stale_dr: true, stale_dr_reason: reason } : {}),
        });
        ok += group.targets.length;
      } catch (e) {
        const d = e.response?.data?.detail;
        if (e.response?.status === 409 && d?.code === 'stale_source_dr') {
          stale.push({ pid, productName: nameOf(pid), base: group.base,
                       targets: group.targets, sourceDr: d.source_delivery_date,
                       conflicts: d.conflicts || [] });
        } else {
          fail += group.targets.length;
          // Surface the real reason instead of a silent counter.
          toast.error(`${nameOf(pid)}: ${(typeof d === 'string' && d) || d?.message || 'Propagation failed'}`);
        }
      }
    }
    return { ok, fail, stale };
  };

  const groupSelected = () => {
    const byPid = {};
    selectedItems.forEach(it => {
      (byPid[it.pid] = byPid[it.pid] || { base: it.base, targets: [] }).targets.push({
        branch_id: it.branch.id,
        ...(it.override && it.override > 0 ? { override_capital: it.override } : {}),
      });
    });
    return byPid;
  };

  const finish = (ok, fail) => {
    if (ok) toast.success(`Propagated to ${ok} branch target${ok !== 1 ? 's' : ''}${fail ? ` (${fail} failed)` : ''}`);
    else if (fail) toast.error('Propagation failed');
    setSel({});
    load();
  };

  const doPropagate = async () => {
    setApplying(true);
    const { ok, fail, stale } = await runPropagate(groupSelected());
    setApplying(false);
    setReviewOpen(false);
    if (stale.length) {
      // Hold the selection so "Force" can retry exactly what was refused.
      setForceReason('');
      setBlocked(stale);
      if (ok) toast.success(`Propagated to ${ok} branch target${ok !== 1 ? 's' : ''}`);
      return;
    }
    finish(ok, fail);
  };

  const doForce = async () => {
    if (!forceReason.trim()) { toast.error('Enter a reason before forcing'); return; }
    setForcing(true);
    const byPid = {};
    blocked.forEach(b => { byPid[b.pid] = { base: b.base, targets: b.targets }; });
    const { ok, fail } = await runPropagate(byPid, { force: true, reason: forceReason.trim() });
    setForcing(false);
    setBlocked([]);
    finish(ok, fail);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm" data-testid="po-matrix-loading">
        <RefreshCw size={15} className="animate-spin mr-2" /> Loading branch capital…
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="po-branch-matrix">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500">
          Tick a branch cell to push this PO&apos;s <span className="font-semibold text-slate-700">PO cost</span> to that branch as their Last Purchase capital.
          Click a product or branch header to select a whole row/column. Type a price in a ticked cell to override — or a quick calc like <span className="font-mono font-semibold">520+50</span> to add freight.
        </p>
        <Badge variant="outline" className="text-xs shrink-0">{branches.length} branches</Badge>
      </div>

      <ScrollArea className="rounded-lg border" style={{ borderColor: 'var(--border-default)' }}>
        <div className="min-w-max max-h-[340px]">
          <table className="text-sm border-collapse w-full">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-20 min-w-[180px]">Product</th>
                <th className="text-right px-3 py-2 min-w-[130px] sticky left-[180px] bg-slate-50 z-20 border-r"
                  style={{ borderColor: 'var(--border-default)' }}>PO Cost (to push)</th>
                {branches.map(b => (
                  <th key={b.id} className="text-center px-2 py-2 min-w-[116px] border-l cursor-pointer hover:bg-slate-100"
                    style={{ borderColor: 'var(--border-default)' }}
                    onClick={() => toggleColumn(b.id)}
                    data-testid={`po-matrix-col-${b.id}`} title="Select whole column">
                    {b.name}
                    {b.region ? <div className="text-[9px] normal-case text-slate-400">{b.region}</div> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(reportProducts || []).map(p => {
                const pid = p.product_id;
                const base = baseByPid[pid] || 0;
                const srcMA = Number(p.new_capital || 0);
                // Show an info badge when the source branch applied moving-average
                // (new_capital differs from po_unit_price by more than ₱0.01)
                const usedMA = p.method === 'moving_average' && Math.abs(srcMA - base) > 0.01;
                const row = sel[pid] || {};
                const nSel = branches.filter(b => row[b.id]?.include).length;
                return (
                  <tr key={pid} className="border-t hover:bg-slate-50/40" style={{ borderColor: 'var(--border-default)' }}
                    data-testid={`po-matrix-row-${pid}`}>
                    {/* Product (click = whole row) */}
                    <td className="px-3 py-2 sticky left-0 bg-white z-10 cursor-pointer" onClick={() => toggleRow(pid)}
                      data-testid={`po-matrix-rowtoggle-${pid}`} title="Select all branches for this product">
                      <div className="font-semibold text-slate-800 truncate max-w-[170px] flex items-center gap-1">
                        <GitBranch size={11} className={nSel ? 'text-[#1A4D2E]' : 'text-slate-300'} />
                        {p.name}
                      </div>
                      <div className="text-[10px] text-slate-400">{p.sku}{nSel ? ` · ${nSel} selected` : ''}</div>
                    </td>
                    {/* PO cost (base to push) */}
                    <td className="px-3 py-2 text-right sticky left-[180px] bg-white z-10 border-r" style={{ borderColor: 'var(--border-default)' }}>
                      <span className="font-mono font-bold text-[#1A4D2E]">{formatPHP(base)}</span>
                      {usedMA && (
                        <div className="text-[9px] text-amber-600 mt-0.5" title={`Source branch applied moving-avg capital (${formatPHP(srcMA)}) — pushing clean PO cost instead`}>
                          src branch used MA ({formatPHP(srcMA)})
                        </div>
                      )}
                    </td>
                    {/* Branch cells */}
                    {branches.map(b => {
                      const cur = curCapital(pid, b.id);
                      const c = row[b.id] || { include: false, override: '' };
                      const rawOv = (c.override ?? '').toString().trim();
                      const ov = evalMathExpr(c.override);
                      const ovInvalid = rawOv !== '' && ov == null;
                      const isExpr = rawOv !== '' && /[+\-*/()]/.test(rawOv);
                      const newCap = ov && ov > 0 ? ov : base;
                      const higher = cur != null && newCap > cur;
                      const lower = cur != null && newCap < cur;
                      const br = scanMap[pid]?.branches?.[b.id];
                      return (
                        <td key={b.id} className={`px-2 py-1.5 text-center border-l align-top ${c.include ? 'bg-emerald-50/70' : ''}`}
                          style={{ borderColor: 'var(--border-default)' }}>
                          <button onClick={() => toggleCell(pid, b.id)}
                            className={`w-full rounded-md border px-1.5 py-1 transition-colors ${c.include ? 'border-[#1A4D2E] bg-emerald-50' : 'border-transparent hover:border-slate-200'}`}
                            data-testid={`po-matrix-cell-${pid}-${b.id}`}>
                            {cur == null ? (
                              <div className="text-[10px] text-slate-300">no branch</div>
                            ) : (
                              <>
                                <div className="text-[9px] text-slate-400">cur</div>
                                <div className="text-xs font-mono text-slate-600">{formatPHP(cur)}</div>
                                <div className="text-[9px] text-slate-300">stk {br?.stock_on_hand ?? 0}</div>
                              </>
                            )}
                          </button>
                          {c.include && (
                            <>
                              <Input value={c.override ?? ''} onChange={e => setOverride(pid, b.id, e.target.value)}
                                placeholder={formatPHP(base).replace('₱', '')} inputMode="text"
                                className={`h-6 w-[72px] mx-auto mt-1 text-[11px] font-mono px-1 text-center ${ovInvalid ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                                title="Type a price or a quick calc, e.g. 520+50 to add freight"
                                data-testid={`po-matrix-override-${pid}-${b.id}`} />
                              {ovInvalid ? (
                                <div className="text-[10px] font-mono font-bold mt-0.5 text-red-500" data-testid={`po-matrix-override-invalid-${pid}-${b.id}`}>= ?</div>
                              ) : (
                                <div className={`text-[10px] font-mono font-bold mt-0.5 flex items-center justify-center gap-0.5 ${higher ? 'text-red-600' : lower ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {isExpr && <span className="text-slate-400">= </span>}{higher && <TrendingUp size={9} />}{lower && <TrendingDown size={9} />}→ {formatPHP(newCap)}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ScrollArea>

      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-slate-500">{selectedCount} branch target{selectedCount !== 1 ? 's' : ''} selected</span>
        <Button size="sm" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" disabled={!selectedCount}
          onClick={openReview} data-testid="po-matrix-propagate-btn">
          <ShieldCheck size={14} className="mr-1.5" /> Propagate selected ({selectedCount})
        </Button>
      </div>

      {/* Review */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md" data-testid="po-matrix-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
              <ShieldCheck size={18} /> Review propagation
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pushing this PO&apos;s <strong>unit cost</strong> to {selectedCount} branch target(s) as Last Purchase capital.
              Source PO: <span className="font-mono">{sourcePoNumber}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto divide-y rounded-lg border" style={{ borderColor: 'var(--border-default)' }}>
            {selectedItems.map((it, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2" data-testid={`po-matrix-review-row-${it.pid}-${it.branch.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{it.productName}</div>
                  <div className="text-[11px] text-slate-400">
                    {it.branch.name} · {it.current != null ? formatPHP(it.current) : 'new'} → {it.override ? 'override' : 'PO cost'}
                  </div>
                </div>
                <div className="font-mono font-bold text-[#1A4D2E] shrink-0">{formatPHP(it.newCap)}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewOpen(false)} data-testid="po-matrix-review-cancel">Cancel</Button>
            <Button className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" disabled={applying}
              onClick={() => setAuthOpen(true)} data-testid="po-matrix-review-confirm">
              {applying ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
              Confirm with PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Newer-DR block. The server refused these targets; forcing needs a
          written reason, which is stored on the capital change and audit log. */}
      <Dialog open={blocked.length > 0} onOpenChange={(o) => { if (!o) setBlocked([]); }}>
        <DialogContent className="max-w-lg" data-testid="po-matrix-stale-dr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={17} /> Newer delivery at these branches
            </DialogTitle>
            <DialogDescription>
              These branches received this product on a later DR than the receipt you
              are pushing. Their capital is more recent than this price — overwriting
              it moves their cost backwards.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-56 overflow-y-auto space-y-2">
            {blocked.map(b => (
              <div key={b.pid} className="rounded-lg border border-amber-300 bg-amber-50/40 p-2"
                data-testid={`stale-dr-row-${b.pid}`}>
                <div className="text-sm font-medium text-slate-800">{b.productName}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                  <CalendarClock size={10} /> Your source DR: {b.sourceDr || 'unknown'}
                </div>
                <div className="mt-1 space-y-0.5">
                  {b.conflicts.map(c => (
                    <div key={c.branch_id} className="text-[11px] text-amber-800">
                      {c.branch_name} — their DR <b>{c.target_dr}</b> is newer
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">
              Reason for overwriting anyway
            </label>
            <Input value={forceReason} onChange={e => setForceReason(e.target.value)}
              placeholder="e.g. supplier reissued the DR with the correct price"
              className="h-8 text-sm" data-testid="stale-dr-reason" />
            <p className="text-[10px] text-slate-400">
              Recorded on each capital change and in the audit log.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlocked([])} data-testid="stale-dr-cancel">
              Leave them alone
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={forcing || !forceReason.trim()} onClick={doForce}
              data-testid="stale-dr-force">
              {forcing ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <AlertTriangle size={14} className="mr-1.5" />}
              Overwrite anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="cross_branch_capital_propagation"
        onVerified={() => { setAuthOpen(false); doPropagate(); }}
      />
    </div>
  );
}
